/**
 * Audit-trail data hooks (Sprint 6 / T-6.14; US-080) over react-query + the
 * typed `api.audit.*` client. Query keys follow the §8 idiom:
 *   ['case', caseNumber, 'audit', params]   — the filtered/paginated list
 *   ['audit', 'integrity']                  — the firm's last cron verdict
 * The verify action is a mutation (an on-demand server-side recompute — it
 * mutates nothing, but it is expensive and explicitly user-triggered).
 *
 * The event list is `STALE_LIVE_MS` fresh (an audit trail is read for "what just
 * happened"); every mutation that writes an audit event also invalidates
 * `['case', caseNumber, 'audit']`, so a write never waits for the window.
 */
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  api,
  type AuditIntegrityResponse,
  type AuditListParams,
  type AuditListResponse,
  type AuditVerifyResponse,
} from '../lib/api.ts';
import { STALE_LIVE_MS } from '../lib/query-client.ts';

export function useCaseAudit(
  caseNumber: string,
  params: AuditListParams = {},
): UseQueryResult<AuditListResponse> {
  return useQuery({
    queryKey: ['case', caseNumber, 'audit', params],
    queryFn: () => api.audit.list(caseNumber, params),
    enabled: caseNumber !== '',
    placeholderData: keepPreviousData,
    staleTime: STALE_LIVE_MS,
  });
}

export function useAuditVerify(
  caseNumber: string,
): UseMutationResult<AuditVerifyResponse, unknown, void> {
  return useMutation({ mutationFn: () => api.audit.verify(caseNumber) });
}

export function useAuditIntegrity(): UseQueryResult<AuditIntegrityResponse> {
  return useQuery({ queryKey: ['audit', 'integrity'], queryFn: () => api.audit.integrity() });
}
